<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8 font-sans text-slate-900 space-y-6">
    
    <!-- EN-TÊTE ET STATISTIQUES GLOBALES -->
    <header class="flex  lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <div class="space-y-1">
        <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
          <span>Medialink Solutions</span>
          <span>/</span>
          <span class="text-blue-600">Parc Écrans & Salles d'attente</span>
        </div>
        <h1 class="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
          Supervision des Écrans 
        </h1>
      </div>

      <!-- Compteurs rapides du parc d'écrans -->
      <div class="flex items-center gap-3">
        <div class="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs">
          <span class="text-slate-400 font-medium">Total :</span> <strong class="text-slate-900 font-bold">{{ screens.length }}</strong>
        </div>
        <div class="px-3.5 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700">
          <span class="font-medium">Actifs :</span> <strong class="font-bold">{{ onlineCount }}</strong>
        </div>
        <div class="px-3.5 py-2 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
          <span class="font-medium">Hors ligne :</span> <strong class="font-bold">{{ offlineCount }}</strong>
        </div>
      </div>
    </header>

    <!-- BARRE DE RECHERCHE ET FILTRES AVANCÉS -->
    <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex  md:flex-row gap-4 items-center justify-between">
      
      <!-- Recherche textuelle -->
      <div class="w-full md:w-96 relative">
        <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input 
          type="text" 
          v-model="search" 
          placeholder="Rechercher par ID, Nom ou Pharmacie..." 
          class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 text-xs font-medium text-slate-700"
        >
      </div>

      <!-- Filtres de statut & pagination -->
      <div class="flex items-center gap-2 w-full md:w-auto justify-end">
        <select 
          v-model="currentFilter" 
          class="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="all">🔍 Tous les statuts</option>
          <option value="online">🟢 Actifs uniquement</option>
          <option value="offline">🔴 Hors ligne uniquement</option>
        </select>

        <select 
          v-model="itemsPerPage" 
          class="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option :value="15">15 par page</option>
          <option :value="25">25 par page</option>
          <option :value="50">50 par page</option>
          <option :value="100">100 par page</option>
        </select>

        <button 
          @click="fetchScreens"
          class="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all"
          title="Rafraîchir"
        >
          <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- TABLEAU DE GESTION DES ÉCRANS -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      
      <!-- État de chargement -->
      <div v-if="loading" class="p-8 space-y-4">
        <div v-for="n in 5" :key="n" class="flex items-center justify-between animate-pulse">
           <div class="flex gap-3 w-1/3">
             <div class="h-9 w-9 bg-slate-100 rounded-xl"></div>
             <div class="space-y-2 flex-1">
               <div class="h-3 bg-slate-100 rounded w-3/4"></div>
               <div class="h-2 bg-slate-100 rounded w-1/2"></div>
             </div>
           </div>
           <div class="h-3 bg-slate-100 rounded w-20"></div>
           <div class="h-8 bg-slate-100 rounded w-28"></div>
        </div>
      </div>

      <!-- Aucun résultat -->
      <div v-else-if="filteredScreens.length === 0" class="p-16 text-center flex flex-col items-center justify-center">
        <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
          <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        </div>
        <h3 class="text-slate-900 font-bold text-sm mb-1">Aucun écran trouvé</h3>
        <p class="text-slate-400 text-xs">Vérifiez vos filtres ou votre recherche.</p>
      </div>

      <!-- Liste avec Table -->
      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50/75 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th class="px-6 py-4">Pharmacie / Agence</th>
                <th class="px-6 py-4">Écran / Infos Device</th>
                <th class="px-6 py-4 text-center">Statut Flux</th>
                <th class="px-6 py-4">Dernière Synchronisation</th>
                <th class="px-6 py-4 text-right">Actions de Pilotage</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-xs">
              <tr v-for="screen in paginatedScreens" :key="screen.id" class="hover:bg-slate-50/60 transition-colors group">
                
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-800">{{ screen.agencyName }}</div>
                  <div class="text-[10px] text-slate-400 font-mono">Zone: {{ screen.identifier || 'N/A' }}</div>
                </td> 

                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 shrink-0">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div class="font-bold text-slate-900">{{ screen.name || 'Sans nom' }}</div>
                      <div class="text-[10px] text-slate-400 font-mono">{{ screen.id }}</div>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-4 text-center">
                  <span :class="statusClasses(screen.status)" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border">
                    <span class="w-1.5 h-1.5 rounded-full bg-current" :class="{ 'animate-pulse': screen.online }"></span>
                    {{ screen.statusLabel }}
                  </span>
                </td>

                <td class="px-6 py-4 text-slate-500 font-medium tabular-nums">{{ screen.lastUpdate }}</td>
                
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-1.5 opacity-90 group-hover:opacity-100">
                    
                    <button 
                      @click="openWaitingRoom(screen)"
                      class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 rounded-xl transition-all shadow-sm"
                      title="Visionner le flux live"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>

                    <button 
                      @click="goToEditor(screen)"
                      class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 rounded-xl transition-all shadow-sm"
                      title="Éditer le design"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>

                    <button 
                      @click="handleRemoteReset(screen)"
                      class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 rounded-xl transition-all shadow-sm"
                      title="Redémarrer à distance"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </button>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination avancée -->
        <div class="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <span class="font-semibold text-slate-500">
               Affichage de <strong class="text-slate-800">{{ paginationStart }}</strong> à <strong class="text-slate-800">{{ paginationEnd }}</strong> sur <strong class="text-slate-800">{{ filteredScreens.length }}</strong> écrans
            </span>
            <div class="flex items-center gap-2">
                <button 
                  @click="currentPage--" 
                  :disabled="currentPage === 1"
                  class="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  Précédent
                </button>
                <span class="px-3 font-bold text-slate-700">
                  Page {{ currentPage }} / {{ totalPages || 1 }}
                </span>
                <button 
                  @click="currentPage++" 
                  :disabled="currentPage >= totalPages"
                  class="px-3.5 py-2 rounded-xl bg-blue-600 text-white font-bold uppercase disabled:opacity-40 hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Suivant
                </button>
            </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

const router = useRouter()
const API_BASE = 'https://api.datafuse.fr/api/super-admin'

const screens = ref([])
const loading = ref(true)
const search = ref('')
const currentFilter = ref('all')
const currentPage = ref(1)
const itemsPerPage = ref(25)

// Compteurs globaux
const onlineCount = computed(() => screens.value.filter(s => s.online).length)
const offlineCount = computed(() => screens.value.filter(s => !s.online).length)

// Filtrage multi-critères (Recherche + Onglet Statut)
const filteredScreens = computed(() => {
  return screens.value.filter(s => {
    const matchesSearch = !search.value || 
      s.id.toLowerCase().includes(search.value.toLowerCase()) || 
      (s.name && s.name.toLowerCase().includes(search.value.toLowerCase())) ||
      (s.agencyName && s.agencyName.toLowerCase().includes(search.value.toLowerCase()))

    let matchesStatus = true
    if (currentFilter.value === 'online') matchesStatus = s.online
    if (currentFilter.value === 'offline') matchesStatus = !s.online

    return matchesSearch && matchesStatus
  })
})

const totalPages = computed(() => Math.ceil(filteredScreens.value.length / itemsPerPage.value))

const paginatedScreens = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return filteredScreens.value.slice(start, start + itemsPerPage.value)
})

const paginationStart = computed(() => {
  if (filteredScreens.value.length === 0) return 0
  return (currentPage.value - 1) * itemsPerPage.value + 1
})

const paginationEnd = computed(() => {
  const end = currentPage.value * itemsPerPage.value
  return end > filteredScreens.value.length ? filteredScreens.value.length : end
})

// Remise à zéro de la page si les filtres changent
watch([search, currentFilter, itemsPerPage], () => {
  currentPage.value = 1
})

const fetchScreens = async () => {
  loading.value = true
  try {
    const token = localStorage.getItem("superAdminToken")
    if (!token) {
      toast.error("Session expirée")
      return
    }

    const res = await fetch(`${API_BASE}/screens`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (!res.ok) throw new Error("Erreur serveur")

    const data = await res.json()
    
    screens.value = data.map(s => ({
      ...s,
      status: s.online ? "active" : "inactive",
      statusLabel: s.online ? "Live Now" : "Déconnecté",
      lastUpdate: s.lastUpdate 
        ? new Date(s.lastUpdate).toLocaleString('fr-FR', { 
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
          })
        : 'Jamais'
    }))
  } catch (err) {
    console.error("Fetch Error:", err)
    toast.error("Échec de la synchronisation du parc")
  } finally {
    loading.value = false
  }
}

const statusClasses = (status) => {
  if (status === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  return 'bg-rose-50 text-rose-700 border-rose-200'
}

const openWaitingRoom = (screen) => {
  router.push({ path: '/waitingRoom', query: { token: screen.connectionToken } })
}

const goToEditor = (screen) => {
  router.push({
    path: `/screen-editor/${screen.agencyId}`,
    query: { deviceId: screen.id }
  })
}

const handleRemoteReset = (screen) => {
  toast.promise(
    new Promise((resolve) => setTimeout(resolve, 2000)),
    {
      pending: `Signal de reset envoyé à ${screen.name}...`,
      success: 'Device réinitialisé 👌',
      error: 'Échec de la communication 🤯'
    }
  )
}

onMounted(() => {
  fetchScreens()
})
</script>